'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Icon, Popover, PopoverContent, PopoverTrigger, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@repo/ui';
import { Calendar } from '@repo/ui/calendar';
import { Calendar as CalendarIcon, Close } from '@repo/ui/icons';
import { format, subDays } from 'date-fns';
import { useState } from 'react';
import { useIsBelowBreakpoint } from '@/hooks/useBreakpoints';
import type { DateRangeValue } from './dateRange';
interface DateRangePreset {
  label: string;
  shortLabel: string;
  value: string;
  getRange: () => DateRangeValue;
}
const DEFAULT_PRESETS: DateRangePreset[] = [{
  label: 'Last 7 days',
  shortLabel: 'Last 7d',
  value: 'last_7_days',
  getRange: () => ({
    from: subDays(new Date(), 6),
    to: new Date()
  })
}, {
  label: 'Last 30 days',
  shortLabel: 'Last 30d',
  value: 'last_30_days',
  getRange: () => ({
    from: subDays(new Date(), 29),
    to: new Date()
  })
}, {
  label: 'Last 90 days',
  shortLabel: 'Last 90d',
  value: 'last_90_days',
  getRange: () => ({
    from: subDays(new Date(), 89),
    to: new Date()
  })
}];
const formatLabel = (value: DateRangeValue | undefined, placeholder: string): string => {
  if (!value?.from) return placeholder;
  const from = format(value.from, 'MMM d, yyyy');
  if (!value.to) return from;
  return `${from} – ${format(value.to, 'MMM d, yyyy')}`;
};
export interface DateRangePickerProps {
  value: DateRangeValue | undefined;
  /** `source` labels the interaction (preset slug, `'custom'`, or `'clear'`). */
  onChange: (value: DateRangeValue | undefined, source: string) => void;
  presets?: DateRangePreset[];
  placeholder?: string;
  side?: 'bottom' | 'left' | 'right' | 'top';
  align?: 'start' | 'center' | 'end';
  disabled?: boolean;
}

/**
 * Presentational date-range picker: a trigger + popover calendar with quick
 * presets. Fully controlled and app-agnostic (no analytics, no data mapping) —
 * the piece meant to be promoted to a shared UI package if a second app needs it.
 */
export const DateRangePicker = t0 => {
  const $ = _c(58);
  const {
    value,
    onChange,
    presets: t1,
    placeholder: t2,
    side: t3,
    align: t4,
    disabled: t5
  } = t0;
  const presets = t1 === undefined ? DEFAULT_PRESETS : t1;
  const placeholder = t2 === undefined ? "All time" : t2;
  const side = t3 === undefined ? "bottom" : t3;
  const align = t4 === undefined ? "end" : t4;
  const disabled = t5 === undefined ? false : t5;
  const [open, setOpen] = useState(false);
  const hasValue = Boolean(value?.from);
  const isMediumScreen = useIsBelowBreakpoint("m");
  const [today, setToday] = useState(_temp);
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = next => {
      if (next) {
        setToday(new Date());
      }
      setOpen(next);
    };
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const handleOpenChange = t6;
  let t7;
  if ($[1] !== onChange) {
    t7 = event => {
      event.stopPropagation();
      onChange(undefined, "clear");
    };
    $[1] = onChange;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const handleClearFromTrigger = t7;
  let t8;
  if ($[3] !== placeholder || $[4] !== value) {
    t8 = formatLabel(value, placeholder);
    $[3] = placeholder;
    $[4] = value;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const t9 = `Filter by date range: ${t8}`;
  let t10;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Icon icon={<CalendarIcon />} className="text-secondary-foreground" />;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  let t11;
  if ($[7] !== placeholder || $[8] !== value) {
    t11 = formatLabel(value, placeholder);
    $[7] = placeholder;
    $[8] = value;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  let t12;
  if ($[10] !== t11) {
    t12 = <span className="text-3xs text-foreground">{t11}</span>;
    $[10] = t11;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] !== handleClearFromTrigger || $[13] !== hasValue) {
    t13 = hasValue && <TooltipProvider><Tooltip><TooltipTrigger asChild={true}><span role="button" onClick={handleClearFromTrigger} className="text-secondary-foreground hover:text-foreground cursor-pointer"><Icon icon={<Close />} /></span></TooltipTrigger><TooltipContent>Remove date range filter</TooltipContent></Tooltip></TooltipProvider>;
    $[12] = handleClearFromTrigger;
    $[13] = hasValue;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== disabled || $[16] !== t12 || $[17] !== t13 || $[18] !== t9) {
    t14 = <PopoverTrigger asChild={true}><Button variant="ghost" size="sm" disabled={disabled} aria-label={t9}>{t10}{t12}{t13}</Button></PopoverTrigger>;
    $[15] = disabled;
    $[16] = t12;
    $[17] = t13;
    $[18] = t9;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== isMediumScreen || $[21] !== onChange || $[22] !== presets) {
    let t16;
    if ($[24] !== isMediumScreen || $[25] !== onChange) {
      t16 = preset => <Button key={preset.value} variant={isMediumScreen ? "outline" : "ghost"} onClick={() => onChange(preset.getRange(), preset.value)}><span className="md:hidden">{preset.shortLabel}</span><span className="max-md:hidden">{preset.label}</span></Button>;
      $[24] = isMediumScreen;
      $[25] = onChange;
      $[26] = t16;
    } else {
      t16 = $[26];
    }
    t15 = presets.map(t16);
    $[20] = isMediumScreen;
    $[21] = onChange;
    $[22] = presets;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[27] !== t15) {
    t16 = <div className="border-border p-xs gap-xs flex flex-row border-b md:flex-col md:border-r md:border-b-0">{t15}</div>;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const t17 = isMediumScreen ? 1 : 2;
  let t18;
  if ($[29] !== onChange) {
    t18 = range => onChange(range?.from ? {
      from: range.from,
      to: range.to
    } : undefined, "custom");
    $[29] = onChange;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== today) {
    t19 = {
      after: today
    };
    $[31] = today;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t17 || $[34] !== t18 || $[35] !== t19 || $[36] !== value) {
    t20 = <Calendar mode="range" numberOfMonths={t17} selected={value} onSelect={t18} disabled={t19} className="max-md:w-full" />;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
    $[36] = value;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  const t21 = !hasValue;
  let t22;
  if ($[38] !== onChange) {
    t22 = () => onChange(undefined, "clear");
    $[38] = onChange;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Icon icon={<Close />} />;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== t21 || $[42] !== t22) {
    t24 = <div className="border-border p-xs flex justify-end border-t"><Button variant="ghost" disabled={t21} onClick={t22}>{t23}Clear</Button></div>;
    $[41] = t21;
    $[42] = t22;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t20 || $[45] !== t24) {
    t25 = <div className="flex flex-col">{t20}{t24}</div>;
    $[44] = t20;
    $[45] = t24;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t16 || $[48] !== t25) {
    t26 = <div className="flex max-md:flex-col">{t16}{t25}</div>;
    $[47] = t16;
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== align || $[51] !== side || $[52] !== t26) {
    t27 = <PopoverContent side={side} align={align} className="border-border w-auto overflow-hidden rounded-lg border p-0 shadow-lg max-md:mx-[calc((var(--radix-popover-content-available-width)-90vw)/2)] max-md:w-[90vw]">{t26}</PopoverContent>;
    $[50] = align;
    $[51] = side;
    $[52] = t26;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== open || $[55] !== t14 || $[56] !== t27) {
    t28 = <Popover open={open} onOpenChange={handleOpenChange}>{t14}{t27}</Popover>;
    $[54] = open;
    $[55] = t14;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  return t28;
};
function _temp() {
  return new Date();
}